// src/components/Dashboard/Cards/DataInsightsCard.jsx

import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'flowbite-react';
import DashboardCard from './DashboardCard';

/**
 * Utility to highlight "Fall 2026" in blue + underline within a string.
 */
function highlightFall2025(label) {
  const term = 'Fall 2025';
  if (!label.includes(term)) return label; // No match, return as-is
  const [before, after] = label.split(term);
  return (
    <>
      {before}
      <span className="text-blue-500 underline">{term}</span>
      {after}
    </>
  );
}

/**
 * Returns the appropriate color (red/green/gray) and arrow (↑/↓) 
 * depending on the difference value (positive/negative/zero).
 * 
 * - Positive difference => red arrow up (↑)
 * - Negative difference => green arrow down (↓)
 * - Zero difference => gray, no arrow
 */
function getDifferenceColorAndArrow(diff) {
  if (diff > 0) {
    return { color: 'text-red-500', arrow: '↑' };
  } else if (diff < 0) {
    return { color: 'text-green-500', arrow: '↓' };
  }
  return { color: 'text-gray-500', arrow: '' };
}

const DataInsightsCard = ({ onHide }) => {
  const [insightsData, setInsightsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate data fetching
    try {
      // Example static data. Replace with real API calls as needed.
      const data = [
        {
          id: '1',
          label: 'Students with a Hold on their Account',
          count: 23,
          difference: 3, // +3 => more holds => bad => red ↑
        },
        {
          id: '2',
          label: 'Students with Low Course Engagement',
          count: 7,
          difference: -2, // -2 => fewer => good => green ↓
        },
        {
          id: '3',
          label: 'Students who Withdrew from a Course',
          count: 3,
          difference: -1, // -1 => fewer => good => green ↓
        },
        {
          id: '4',
          label: 'Students Not Enrolled for Fall 2025',
          count: 42,   // Actual unenrolled number
          total: 367,  // Out of 367 total students
          difference: 5, // +5 => more not enrolled => bad => red ↑
        },
      ];
      setInsightsData(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching data insights:', error);
      setIsLoading(false);
      toast.error('Failed to load data insights.');
    }
  }, []);

  return (
    <DashboardCard
      title={<span className="font-medium">Data Insights</span>}
      onHide={onHide}
      isLoading={isLoading}
    >
      {!isLoading && (
        insightsData.length > 0 ? (
          <div className="flex space-x-4 overflow-x-auto">
            {insightsData.map((item) => {
              const { color, arrow } = getDifferenceColorAndArrow(item.difference);

              // If "total" is defined, we show "X out of total" 
              // otherwise just show the count.
              const displayCount = item.total
                ? `${item.count}/${item.total}`
                : item.count;

              return (
                <div
                  key={item.id}
                  className="cursor-pointer bg-white dark:bg-gray-800
                             border border-gray-300 dark:border-gray-700
                             rounded-lg p-2 w-1/4 flex flex-col justify-between
                             relative transition-all duration-300"
                >
                  <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {/* Highlight Fall 2026 if present in label */}
                    {highlightFall2025(item.label)}
                  </h5>
                  <div className="flex items-center text-sm text-gray-900 dark:text-white">
                    <p className="text-base font-bold">
                      {displayCount}
                    </p>
                    <span className="ml-1 text-gray-500">students</span>
                    {item.difference !== 0 && (
                      <span className={`ml-2 text-xs font-medium ${color}`}>
                        {arrow} {Math.abs(item.difference)} vs yesterday
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-gray-600 dark:text-gray-400 mb-3">
              No data insights available.
            </p>
            <Button
              className="text-blue-700 hover:text-white border border-blue-700
                         hover:bg-blue-800 focus:ring-4 focus:outline-none
                         focus:ring-blue-300 font-medium rounded-lg text-sm
                         px-5 py-2.5 text-center dark:border-blue-500
                         dark:text-blue-500 dark:hover:text-white
                         dark:hover:bg-blue-500 dark:focus:ring-blue-800"
            >
              Generate Insights
            </Button>
          </div>
        )
      )}
    </DashboardCard>
  );
};

export default DataInsightsCard;
